/**
 * A group combination window in the By Group day view (#1579 part 3).
 *
 * CombinationBlock is the translucent area behind the shift bars that spans the rows
 * of the unit. CombinationBlockLabel sits on top at the block's top-left corner and is
 * the only click target, so the shift bars under the block stay clickable.
 *
 * A previewed suggestion (#1579 part 4) uses the same block with the preview style and
 * CombinationPreviewLabel, which takes no clicks at all.
 */

import type { CSSProperties } from 'react';
import { useLingui } from '@lingui/react';
import { AlertTriangle, Lightbulb, Users } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { UnitCoverage } from '@/types/groupCombinations';
import { combinationName, summarizeUnit } from './combinationLayout';

export interface CombinationBlockPosition {
  top: number;
  height: number;
  /** Horizontal position within the timeline column, in percent. */
  leftPercent: number;
  widthPercent: number;
  /** Width of the resource name column plus the gap before the timeline, in px. */
  timelineOffset: number;
}

const blockStyle = (p: CombinationBlockPosition): CSSProperties => ({
  top: p.top,
  height: p.height,
  left: `calc(${p.timelineOffset}px + (100% - ${p.timelineOffset}px) * ${p.leftPercent / 100})`,
  width: `calc((100% - ${p.timelineOffset}px) * ${p.widthPercent / 100})`,
});

const labelPositionStyle = (p: CombinationBlockPosition): CSSProperties => ({
  top: p.top + 2,
  left: `calc(${p.timelineOffset}px + (100% - ${p.timelineOffset}px) * ${p.leftPercent / 100} + 2px)`,
});

export function CombinationBlock({
  position,
  overCapacity,
  preview = false,
}: {
  position: CombinationBlockPosition;
  overCapacity: boolean;
  /** Draws a suggested combination that is not saved. */
  preview?: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      data-testid={preview ? 'combination-preview-block' : 'combination-block'}
      className={cn(
        'pointer-events-none absolute rounded-md border-2 border-dashed',
        preview
          ? 'border-info-on-surface/60 bg-info-muted'
          : overCapacity
            ? 'border-destructive-on-surface/60 bg-destructive-muted'
            : 'border-primary/60 bg-primary/10'
      )}
      style={blockStyle(position)}
    />
  );
}

export interface CombinationBlockLabelProps {
  unit: UnitCoverage;
  hostName: string;
  memberNames: string[];
  position: CombinationBlockPosition;
  /** Opens the edit dialog. Absent for users who cannot edit. */
  onClick?: () => void;
}

export function CombinationBlockLabel({
  unit,
  hostName,
  memberNames,
  position,
  onClick,
}: CombinationBlockLabelProps) {
  const { i18n } = useLingui();
  const summary = summarizeUnit(unit);
  const name = combinationName(hostName, memberNames);
  const capacity = unit.host_capacity;

  const lines = {
    host: i18n._({ id: 'shifts.combination.block.inHost', message: 'in {host}', values: { host: hostName } }),
    children:
      capacity != null
        ? i18n._({
            id: 'shifts.combination.block.childrenCapacity',
            message: '{children}/{capacity} children',
            values: { children: summary.peakChildren, capacity },
          })
        : i18n._({
            id: 'shifts.combination.block.children',
            message: '{children} children',
            values: { children: summary.peakChildren },
          }),
    staff: i18n._({
      id: 'shifts.combination.block.staff',
      message: 'Staff {scheduled}/{required}',
      values: { scheduled: summary.minScheduled, required: summary.maxRequired },
    }),
    saves: i18n._({
      id: 'shifts.combination.block.saves',
      message: '{count, plural, one {saves # staff} other {saves # staff}}',
      values: { count: unit.staff_saved },
    }),
  };
  const range = i18n._({
    id: 'shifts.combination.block.savedRange',
    message: 'Staff saved per slot: {min} to {max}',
    values: { min: unit.staff_saved, max: unit.staff_saved_max },
  });
  const overCapacityText = i18n._({ id: 'shifts.combination.block.overCapacity', message: 'Over capacity' });
  const shortStaffText = i18n._({ id: 'shifts.combination.block.shortStaff', message: 'Short staff' });

  const content = (
    <>
      <span className="flex max-w-full items-center gap-1 font-semibold">
        <Users className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="min-w-0 truncate">{name}</span>
      </span>
      <span className="max-w-full truncate text-muted-foreground">
        {lines.host}, {lines.children}, {lines.staff}
      </span>
      <span className="flex flex-wrap items-center gap-1">
        <span className="font-medium text-primary">{lines.saves}</span>
        {unit.over_capacity && (
          <span className="flex items-center gap-0.5 text-destructive-on-surface">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {overCapacityText}
          </span>
        )}
        {summary.shortStaffed && (
          <span className="flex items-center gap-0.5 text-warning-on-surface">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {shortStaffText}
          </span>
        )}
      </span>
    </>
  );

  const labelClass =
    'absolute z-20 flex max-w-[16rem] flex-col items-start gap-0.5 rounded-md border bg-background/95 px-2 py-1 text-left text-xs leading-tight shadow-sm';
  const labelStyle = labelPositionStyle(position);

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          {onClick ? (
            <button
              type="button"
              data-testid="combination-block-label"
              className={cn(labelClass, 'cursor-pointer hover:bg-muted')}
              style={labelStyle}
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              {content}
            </button>
          ) : (
            <div data-testid="combination-block-label" className={labelClass} style={labelStyle} tabIndex={0}>
              {content}
            </div>
          )}
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <div className="font-semibold">
              {name}, {unit.start}-{unit.end}
            </div>
            <div>{range}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface CombinationPreviewLabelProps {
  name: string;
  staffSaved: number;
  position: CombinationBlockPosition;
}

/** The label of a previewed suggestion. It ignores the pointer, so every shift bar stays clickable. */
export function CombinationPreviewLabel({ name, staffSaved, position }: CombinationPreviewLabelProps) {
  const { i18n } = useLingui();
  return (
    <div
      data-testid="combination-preview-label"
      className="pointer-events-none absolute z-20 flex max-w-[16rem] flex-col items-start gap-0.5 rounded-md border border-dashed border-info-on-surface/60 bg-background/95 px-2 py-1 text-left text-xs leading-tight shadow-sm"
      style={labelPositionStyle(position)}
    >
      <span className="flex items-center gap-1 font-semibold text-info-on-surface">
        <Lightbulb className="h-3 w-3 shrink-0" aria-hidden="true" />
        {i18n._({ id: 'shifts.suggestions.previewLabel', message: 'Suggestion' })}
      </span>
      <span className="max-w-full truncate">{name}</span>
      <span className="font-medium">
        {i18n._({
          id: 'shifts.combination.block.saves',
          message: '{count, plural, one {saves # staff} other {saves # staff}}',
          values: { count: staffSaved },
        })}
      </span>
    </div>
  );
}
